import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import Button from '../../../../shared/components/ui/Button';
import FormInput from '../../../../shared/components/ui/FormInput';
import toast from 'react-hot-toast';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import { addTaskUpdate, uploadFilesToS3 } from '../../../services/api/implementation/impl';

const AddTaskUpdate = ({ selectedTask, onSuccess, projectId }) => {
  const [loading, setLoading] = useState(false);
  const [updateDetails, setUpdateDetails] = useState('');
  const [files, setFiles] = useState([]);
  const [consumedMaterials, setConsumedMaterials] = useState([]);
  const [machines, setMachines] = useState([]);
  const [workCompleted, setWorkCompleted] = useState('');
  const [skilledLabour, setSkilledLabour] = useState('');
  const [unskilledLabour, setUnskilledLabour] = useState('');
  const [materials, setMaterials] = useState([]);

  const userID = Number(Cookies.get('userId'));

  const {
    register,
    formState: { errors },
    setValue,
    watch
  } = useForm();

  // Extract materials from selectedTask's liveTask.materialTaskMapping
  useEffect(() => {
    if (
      selectedTask?.liveTask?.materialTaskMapping &&
      selectedTask?.liveTask?.materialTaskMapping?.length > 0
    ) {
      // Extract materials from the task's material mapping
      const taskMaterials = selectedTask?.liveTask?.materialTaskMapping.map((mapping) => ({
        item_id: mapping.item_id,
        item: mapping.item,
        unit: mapping.unit,
        quantity: mapping.quantity
      }));
      setMaterials(taskMaterials);

      // Initialize consumed materials with one empty row
      setConsumedMaterials([
        { itemId: null, selectedUnit: '', quantity: '', allocatedQuantity: 0 }
      ]);
    } else {
      // No materials available
      setMaterials([]);
      setConsumedMaterials([]);
    }

    // Initialize machines
    setMachines([{ name: '', fuelUsed: '' }]);
  }, [selectedTask]);

  // Get available materials for a specific row (excluding already selected items)
  const getAvailableMaterialsForRow = (currentIndex) => {
    if (!materials || materials.length === 0) {
      return [];
    }

    // Get all selected item IDs except the current row
    const selectedItemIds = consumedMaterials
      .filter((item, index) => item.itemId !== null && index !== currentIndex)
      .map((item) => item.itemId);

    // Filter out already selected materials
    const availableMaterials = materials.filter(
      (material) => !selectedItemIds.includes(material.item_id)
    );

    return availableMaterials.map((material) => ({
      label: material?.item?.name || 'Unnamed Item', // Use item name for display
      id: material?.item_id // Use item_id as the value
    }));
  };

  // File upload handlers
  const handleDocumentSelection = useCallback(async (event) => {
    try {
      const selectedFiles = Array.from(event.target.files);
      if (selectedFiles.length === 0) return;

      console.log('Selected files:', selectedFiles);

      try {
        const uploadedFiles = await uploadFilesToS3(selectedFiles);
        console.log('Uploaded files:', uploadedFiles);
        setFiles((prevFiles) => [...prevFiles, ...uploadedFiles]);
        toast.success('Files uploaded successfully!');
      } catch (err) {
        console.log('Error uploading files:', err);
        toast.error(err?.response?.data?.message || err?.message || 'Failed to upload files');
      }
    } catch (err) {
      console.log('Error:', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error selecting files');
    }
  }, []);

  const removeFile = (index) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  // Material handlers
  const handleQuantityChange = (index, text) => {
    const updatedConsumedMaterials = [...consumedMaterials];
    const numericValue = text.replace(/[^0-9.]/g, '');
    const parts = numericValue.split('.');
    const formattedValue =
      parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : numericValue;

    updatedConsumedMaterials[index] = {
      ...updatedConsumedMaterials[index],
      quantity: formattedValue
    };
    setConsumedMaterials(updatedConsumedMaterials);
  };

  const addMaterialRow = () => {
    setConsumedMaterials([
      ...consumedMaterials,
      { itemId: null, quantity: '', selectedUnit: '', allocatedQuantity: 0 }
    ]);
  };

  const removeMaterialRow = (index) => {
    const updatedConsumedMaterials = consumedMaterials.filter((_, i) => i !== index);
    if (updatedConsumedMaterials.length === 0) {
      setConsumedMaterials([
        { itemId: null, quantity: '', selectedUnit: '', allocatedQuantity: 0 }
      ]);
    } else {
      setConsumedMaterials(updatedConsumedMaterials);
    }
  };

  const handleMaterialChange = (index, selectedItem) => {
    console.log('handleMaterialChange called with:', { index, selectedItem });

    const itemId = selectedItem?.id;
    console.log('itemId:', itemId);

    const selectedMaterial = materials.find((mat) => mat.item_id === itemId);
    console.log('selectedMaterial:', selectedMaterial);

    const updatedConsumedMaterials = [...consumedMaterials];
    updatedConsumedMaterials[index] = {
      ...updatedConsumedMaterials[index],
      itemId,
      selectedUnit: selectedMaterial?.unit || '', // Use the unit from the mapping
      allocatedQuantity: selectedMaterial?.quantity || 0
    };
    console.log('updatedConsumedMaterials:', updatedConsumedMaterials);
    setConsumedMaterials(updatedConsumedMaterials);
  };

  // Machine handlers
  const handleMachineNameChange = (index, text) => {
    const updatedMachines = [...machines];
    updatedMachines[index] = {
      ...updatedMachines[index],
      name: text
    };
    setMachines(updatedMachines);
  };

  const handleFuelUsedChange = (index, text) => {
    const updatedMachines = [...machines];
    const numericValue = text.replace(/[^0-9.]/g, '');
    const parts = numericValue.split('.');
    const formattedValue =
      parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : numericValue;

    updatedMachines[index] = {
      ...updatedMachines[index],
      fuelUsed: formattedValue
    };
    setMachines(updatedMachines);
  };

  const addMachineRow = () => {
    setMachines([...machines, { name: '', fuelUsed: '' }]);
  };

  const removeMachineRow = (index) => {
    const updatedMachines = machines.filter((_, i) => i !== index);
    if (updatedMachines.length === 0) {
      setMachines([{ name: '', fuelUsed: '' }]);
    } else {
      setMachines(updatedMachines);
    }
  };

  // Numeric input handlers
  const handleNumericChange = (setter, text) => {
    const numericValue = text.replace(/[^0-9.]/g, '');
    const parts = numericValue.split('.');
    const formattedValue =
      parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : numericValue;
    setter(formattedValue);
  };

  // Submit handler
  const submitUpdate = async () => {
    if (loading) return;
    setLoading(true);

    if (workCompleted === '' || workCompleted === '0') {
      toast.error('Work Completed percentage is mandatory');
      setLoading(false);
      return;
    }

    // Validate materials - if item is selected, quantity must be entered
    const invalidMaterials = consumedMaterials.some(
      (material) =>
        material.itemId !== null && (material.quantity === '' || material.quantity === '0')
    );

    if (invalidMaterials) {
      toast.error('Quantity is required for all selected materials');
      setLoading(false);
      return;
    }

    // Validate machines - if fuel is entered, machine name must be provided
    const invalidMachinery = machines.some(
      (machine) => machine.fuelUsed !== '' && machine.name === ''
    );

    if (invalidMachinery) {
      toast.error('Machine name is required when fuel is specified');
      setLoading(false);
      return;
    }

    const validConsumedMaterials = consumedMaterials
      .filter((item) => item.itemId !== null && item.quantity !== '' && item.selectedUnit !== '')
      .map((item) => ({
        item_id: item.itemId,
        quantity: parseFloat(item.quantity),
        unit: item.selectedUnit
      }));

    const validMachines = machines
      .filter((machine) => machine.name !== '')
      .map((machine) => {
        const machineData = { name: machine.name };
        if (machine.fuelUsed !== '') {
          machineData.fuelUsed = parseFloat(machine.fuelUsed);
        }
        return machineData;
      });

    const payload = {
      name: selectedTask?.name,
      task_id: selectedTask?.task_id,
      description: updateDetails,
      images: files,
      materials: validConsumedMaterials,
      machines: validMachines,
      workCompleted: parseFloat(workCompleted),
      skilledLabour: skilledLabour !== '' ? parseFloat(skilledLabour) : 0,
      unskilledLabour: unskilledLabour !== '' ? parseFloat(unskilledLabour) : 0,
      dailyTask_id: selectedTask?.id
    };

    console.log('PAYLOAD:', JSON.stringify(payload, null, 2));

    try {
      await addTaskUpdate(payload);
      toast.success('Task update submitted successfully!');

      mixpanel.track('Task Update Submitted', {
        user_id: userID,
        task_id: selectedTask?.task_id,
        materials_count: validConsumedMaterials.length,
        machines_count: validMachines.length,
        timestamp: new Date().toISOString()
      });

      onSuccess();
    } catch (err) {
      console.error('Failed to add task update:', err);
      toast.error(err?.response?.data?.message || err?.message || 'Failed to add task update');

      mixpanel.track('Task Update Failed', {
        user_id: userID,
        task_id: selectedTask?.task_id,
        error_message: err?.response?.data?.message || err?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full">
      <div className="space-y-6">
        {/* Task Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Task</label>
          <input
            type="text"
            value={selectedTask?.name || ''}
            disabled
            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
          />
        </div>

        {/* Update Details */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Update Details</label>
          <textarea
            value={updateDetails}
            onChange={(e) => setUpdateDetails(e.target.value)}
            placeholder="Update description"
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Material Consumption Section */}
        {materials && materials.length > 0 && (
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center mb-4">
              <svg className="w-6 h-6 text-[#346C75] mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
              </svg>
              <span className="text-lg font-medium text-gray-700">
                Material Consumption Tracking
              </span>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Track consumed materials against allocated quantities from the task
            </p>

            {consumedMaterials.map((consumedItem, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4 mb-4">
                {/* Material Selection - Always show dropdown */}
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Material</label>
                  <select
                    value={consumedItem.itemId || ''}
                    onChange={(e) => {
                      const selectedId = e.target.value;
                      if (selectedId) {
                        const selectedMaterial = materials.find(
                          (mat) => mat.item_id === parseInt(selectedId)
                        );
                        handleMaterialChange(index, { id: parseInt(selectedId) });
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">Select Item</option>
                    {getAvailableMaterialsForRow(index).map((material) => (
                      <option key={material.id} value={material.id}>
                        {material.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Unit and Quantity - Always show but disable if no item selected */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                    <input
                      type="text"
                      value={consumedItem.selectedUnit || ''}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                    <input
                      type="text"
                      value={consumedItem.quantity}
                      onChange={(e) => handleQuantityChange(index, e.target.value)}
                      placeholder="Qty"
                      disabled={!consumedItem.itemId}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        !consumedItem.itemId ? 'bg-gray-50' : ''
                      }`}
                    />
                  </div>
                  {consumedMaterials.length > 1 && (
                    <div className="flex items-end">
                      <button
                        onClick={() => removeMaterialRow(index)}
                        className="w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600">
                        ×
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {getAvailableMaterialsForRow(consumedMaterials.length).length > 0 && (
              <button
                onClick={addMaterialRow}
                className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-[#346C75] hover:text-[#346C75] transition-colors flex items-center justify-center">
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                    clipRule="evenodd"
                  />
                </svg>
                Add More
              </button>
            )}
          </div>
        )}

        {/* Machinery Section */}
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center mb-4">
            <svg className="w-6 h-6 text-[#346C75] mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
            </svg>
            <span className="text-lg font-medium text-gray-700">Machinery Used</span>
          </div>

          {machines.map((machine, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <svg
                    className="w-5 h-5 text-[#346C75] mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20">
                    <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                  </svg>
                  <span className="text-sm font-medium text-gray-700">Machine #{index + 1}</span>
                </div>
                {machines.length > 1 && (
                  <button
                    onClick={() => removeMachineRow(index)}
                    className="w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600">
                    ×
                  </button>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Machine Name
                  </label>
                  <input
                    type="text"
                    value={machine.name}
                    onChange={(e) => handleMachineNameChange(index, e.target.value)}
                    placeholder="e.g., JCB, Excavator"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fuel Used (L)
                  </label>
                  <input
                    type="text"
                    value={machine.fuelUsed}
                    onChange={(e) => handleFuelUsedChange(index, e.target.value)}
                    placeholder="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          ))}

          <button
            onClick={addMachineRow}
            className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-[#346C75] hover:text-[#346C75] transition-colors flex items-center justify-center">
            <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                clipRule="evenodd"
              />
            </svg>
            Add Machine
          </button>
        </div>

        {/* Work Progress Section */}
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center mb-4">
            <svg className="w-6 h-6 text-[#346C75] mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z" />
              <path d="M12 2.252A8.014 8.014 0 0117.748 8H12V2.252z" />
            </svg>
            <span className="text-lg font-medium text-gray-700">Work Progress</span>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Work Completed (%)
            </label>
            <input
              type="text"
              value={workCompleted}
              onChange={(e) => handleNumericChange(setWorkCompleted, e.target.value)}
              placeholder="0"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Labour Section */}
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center mb-4">
            <svg className="w-6 h-6 text-[#346C75] mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
            </svg>
            <span className="text-lg font-medium text-gray-700">Labour Details</span>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Skilled</label>
              <input
                type="text"
                value={skilledLabour}
                onChange={(e) => handleNumericChange(setSkilledLabour, e.target.value)}
                placeholder="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Unskilled</label>
              <input
                type="text"
                value={unskilledLabour}
                onChange={(e) => handleNumericChange(setUnskilledLabour, e.target.value)}
                placeholder="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* File Upload Section */}
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center mb-4">
            <svg className="w-6 h-6 text-[#346C75] mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
            <span className="text-lg font-medium text-gray-700">Add Attachments</span>
          </div>

          <div className="flex items-center space-x-4 mb-4">
            <label className="cursor-pointer">
              <input
                type="file"
                multiple
                onChange={handleDocumentSelection}
                className="hidden"
                accept="*/*"
              />
              <div className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center">
                <svg
                  className="w-5 h-5 text-[#346C75] mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                Upload Files
              </div>
            </label>
          </div>

          {/* File Preview */}
          {files.length > 0 && (
            <div className="grid grid-cols-4 gap-4">
              {files.map((file, index) => (
                <div key={index} className="relative">
                  {file.type.startsWith('image/') ? (
                    <img
                      src={file.url}
                      alt={file.name}
                      className="w-full h-24 object-cover rounded-lg"
                    />
                  ) : (
                    <div className="w-full h-24 bg-gray-100 rounded-lg flex items-center justify-center">
                      <span className="text-sm text-gray-600">{file.type.split('/')[1]}</span>
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 truncate">
                    {file.name}
                  </div>
                  <button
                    onClick={() => removeFile(index)}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600">
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <Button onClick={submitUpdate} disabled={loading} displayType="primary">
            {loading ? 'Submitting...' : 'Submit Update'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddTaskUpdate;
